import {
  useRef,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";

//Components
import { FormControl, TextField, Modal } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import SingleDropDown from "../SingleDropDown";
import CheckboxWithLabel from "../CheckboxWithLabel";
import MultiDropDownObjects from "../MultiDropDownObjects";
import PhoneInput from "../PhoneInput";
import PersonHistoryAlert from "../PersonHistoryAlert";
import AddressWithAlertAndHmi from "../AddressWithAlertAndHmi";

//Contexts
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { MutationContext } from "../../Contexts/MutationContext";
import { UserContext } from "../../Contexts/UserContext";
import { SetupContext } from "../../Contexts/SetupContext";

//Fetching
import { useLazyQuery, useMutation } from "@apollo/client";
import { ADD_OR_UPDATE_ADDRESS } from "../../Mutations/AddOrUpdateAddress";

//Styling
import { useStyles } from "../../Styles/styles";
import silhouette from "../../Assets/silhouette.png";

//Utils
import {
  parseObjectArrayIdString,
  handleUuid,
  formatNameFromPersonObj,
} from "../../utils/utils";
import PersonHistoryModal from "../PersonHistoryModal";
import { PERSON_ACTIONS } from "../../Actions/PersonActions";
import PersonSearch from "../PersonSearch";
import { shouldBeLocked } from "../../utils/Roles";
import ImageCarousel from "../ImageCarousel";
import RelationshipModal from "../RelationshipModal";
import Attachments from "../Attachment/Attachment";
import { createEmptyIncident } from "../../utils/EmptyObjects";
import PersonDetailButtons from "./PersonDetailButtons";
import { KNOWN_ASSOCIATES_LENGTH } from "../../Queries/KnownAssociatesLength";
import {
  useHandleAddPersonIncidentSpecific,
  useHandleRemovePerson,
} from "./hooks";
import LicenseNumberAndState from "../LicenseNumberAndState/LicenseNumberAndState";
import PersonIncidentSpecific from "./PersonIncidentSpecific/PersonIncidentSpecific";

import moment from "moment";

const PersonDetailWrapper = props => {
  //The purpose of this wrapper is to pass default pis values to the PersonDetail component. DefaultProps doesn't handle nested objects.
  let localPis = JSON.parse(JSON.stringify(props.pis));
  localPis.bac =
    localPis?.bac === null || localPis?.bac === undefined ? "" : localPis?.bac;
  localPis.incidentInvolvement =
    localPis?.incidentInvolvement === null ||
    localPis?.incidentInvolvement === undefined
      ? null
      : localPis?.incidentInvolvement;
  localPis.person =
    localPis?.person === null || localPis?.person === undefined
      ? null
      : localPis?.person;
  // localPis.alcoholUse = (localPis?.alcoholUse === null || localPis?.alcoholUse === undefined) ? false : localPis?.alcoholUse
  localPis.clothing =
    localPis?.clothing === null || localPis?.clothing === undefined
      ? ""
      : localPis?.clothing;
  localPis.computerUse = !localPis?.computerUse ? false : localPis?.computerUse;
  localPis.docStatus =
    localPis?.docStatus === null || localPis?.docStatus === undefined
      ? null
      : localPis?.docStatus;
  localPis.drugTypes =
    localPis?.drugTypes === null || localPis?.drugTypes === undefined
      ? []
      : localPis?.drugTypes;
  // localPis.drugUse = (localPis?.drugUse === null || localPis?.drugUse === undefined) ? false : localPis?.drugUse
  localPis.ibrResidentType =
    localPis?.ibrResidentType === null ||
    localPis?.ibrResidentType === undefined
      ? null
      : localPis?.ibrResidentType;
  localPis.injuryTypes =
    localPis?.injuryTypes === null || localPis?.injuryTypes === undefined
      ? []
      : localPis?.injuryTypes;
  localPis.opiateBlocker =
    localPis?.opiateBlocker === null || localPis?.opiateBlocker === undefined
      ? false
      : localPis?.opiateBlocker;
  localPis.orphanage =
    localPis?.orphanage === null || localPis?.orphanage === undefined
      ? false
      : localPis?.orphanage;
  localPis.paperwork =
    localPis?.paperwork === null || localPis?.paperwork === undefined
      ? null
      : localPis?.paperwork;
  localPis.sealed =
    localPis?.sealed === null || localPis?.sealed === undefined
      ? false
      : localPis?.sealed;
  // localPis.statement = (localPis?.statement === null || localPis?.statement === undefined) ? "" : localPis?.statement
  localPis.statementTakenBy =
    localPis?.statementTakenBy === null ||
    localPis?.statementTakenBy === undefined
      ? null
      : localPis?.statementTakenBy;
  localPis.statementType =
    localPis?.statementType === null || localPis?.statementType === undefined
      ? null
      : localPis?.statementType;
  localPis.uuid =
    localPis?.uuid === null || localPis?.uuid === undefined
      ? null
      : localPis?.uuid;
  localPis.victimType = !localPis?.victimType ? null : localPis?.victimType;
  localPis.weaponType =
    localPis?.weaponType === null || localPis?.weaponType === undefined
      ? null
      : localPis?.weaponType;

  return <PersonDetail pis={localPis} {...props} />;
};

const stateToMutation = {
  bac: "bac",
  incidentInvolvement: "incidentInvolvementId",
  victimType: "victimTypeId",
  clothing: "clothing",
  computerUse: "computerUse",
  docStatus: "docStatusId",
  drugTypes: "drugTypeIds",
  ibrResidentType: "ibrResidentTypeId",
  injuryTypes: "injuryTypeIds",
  opiateBlocker: "opiateBlocker",
  orphanage: "orphanage",
  paperwork: "paperworkId",
  statementTakenBy: "statementTakenById",
  statementType: "statementTypeId",
  weaponType: "weaponTypeId",
  sealed: "sealed",
  aliasName: "aliasName",
  buildType: "buildTypeId",
  cell: "cell",
  deceased: "deceased",
  distinguishingCharacteristics: "distinguishingCharacteristics",
  driversLicense: "driversLicense",
  email: "email",
  employer: "employer",
  employerAddress: "employerAddressId",
  eyeColor: "eyeColorId",
  facialHairType: "facialHairTypeId",
  fbiNumber: "fbiNumber",
  firstName: "firstName",
  fishWildlifeLicense: "fishWildlifeLicense",
  gender: "genderId",
  hairColor: "hairColorId",
  hairLength: "hairLengthId",
  height: "height",
  homeAddress: "homeAddressId",
  ibrArresteeRaceType: "ibrArresteeRaceTypeId",
  juvenile: "juvenile",
  lastName: "lastName",
  licenseState: "licenseStateId",
  mailingAddress: "mailingAddressId",
  mentalHealthAlert: "mentalHealthAlert",
  middleName: "middleName",
  nibrsGender: "nibrsGenderId",
  notes: "notes",
  organization: "organization",
  phone: "phone",
  pobAddress: "pobAddressId",
  race: "raceId",
  residentType: "residentTypeId",
  sid: "sid",
  substanceAbuseAlert: "substanceAbuseAlert",
  suffix: "suffix",
  violenceAlert: "violenceAlert",
  waiverFine: "waiverFine",
  weaponsAlert: "weaponsAlert",
  weight: "weight",
  work: "work",
  dob: "dob",
  gangAffiliateds: "gangAffiliatedsIds",
  groups: "groupIds",
  school: "school",
};

const PersonDetail = ({
  getPissForIncident,
  pis,
  pisIndex,
  personIndex,
  incidentId,
  primaryOfficerId,
  incidentUuid,
  incidentIndex,
  personTab,
  setPersonTab,
  peopleLength,
  incidentOriId,
  inIncident,
  handlePersonOpening,
  handleIncidentOpening,
  handleTicketOpening,
  disabled = undefined,
  userRoles,
  setPausePolling,
  createNewIncident,
  setPopper,
  incidentPeople = [],
  incidentCallTime = "",
  ...props
}) => {
  const classes = useStyles();
  const { setup, saOris } = useContext(SetupContext);
  const { user, onlineState, tokenDisabled } = useContext(UserContext);
  const { incidentDispatch, personDispatch, incidents } =
    useContext(IncidentsContext);
  const {
    UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
    UPDATE_PERSON_IN_PIS,
    ADD_OR_UPDATE_PIS,
  } = INCIDENT_ACTIONS;
  const { UPDATE_PERSON_PERSONAL_ATTRIBUTE, UPDATE_PERSON_ATTRIBUTE } =
    PERSON_ACTIONS;

  const [showPersonSearch, setShowPersonSearch] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [labelWidth, setLabelWidth] = useState(0);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const inputLabel = useRef(null);

  const handleRemovePerson = useHandleRemovePerson(incidents[incidentIndex]);
  const handleAddPISToIncident = useHandleAddPersonIncidentSpecific(
    incidents[incidentIndex],
    () => setPersonTab(peopleLength),
  );

  const handleAddClick = async () => {
    try {
      const { success, errors } = await handleAddPISToIncident();
      if (errors.length > 0) {
        alert(errors.join("\n\n"));
      } else if (success) {
        return;
      } else {
        alert("An error occurred while adding the person.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred while adding the person in catch.");
    }
  };

  const handleRemoveClick = async () => {
    try {
      const { success, cancelled, errors } = await handleRemovePerson(pis);
      if (cancelled) return;
      if (errors.length > 0) {
        alert(errors.join("\n\n"));
      } else if (success) {
        setPersonTab(personTab > 0 ? personTab - 1 : 0);
      } else {
        alert("An error occurred while removing the person.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred while removing the person in catch.");
    }
  };
  const [getKnownAssociatesLength, { data: asscData }] = useLazyQuery(
    KNOWN_ASSOCIATES_LENGTH,
    {
      context: { serializationKey: "incidentQueue" },
      variables: { personId: pis?.person?.id, originatorId: user?.id },
      onError: error => {
        console.error(JSON.stringify(error));
      },
    },
  );
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );

  const knownAssociatesNumber = useMemo(() => {
    if (asscData) {
      return asscData[Object.keys(asscData)[0]]?.length;
    } else {
      return null;
    }
  }, [asscData]);

  const {
    // updatePisStatement, updatePisPerson, updatePisAlcoholUse, updatePisDrugUse,  updatePersonCell, updatePersonPhone,
    // updatePisBac,
    // updatePisIncidentInvolvement,
    // updatePisClothing,
    // updatePisComputerUse,
    // updatePisDocStatus,
    // updatePisDrugTypes,
    // updatePisIbrResidentType,
    // updatePisInjuryTypes,
    // updatePisOpiateBlocker,
    // updatePisOrphanage,
    // updatePisPaperwork,
    // updatePisStateTakenBy,
    // updatePisStatementType,
    // updatePisVictimType,
    // updatePisWeaponType,
    updatePersonOrganization,
    updatePersonFirstName,
    updatePersonMiddleName,
    updatePersonLastName,
    updatePersonSuffix,
    updatePersonJuvenile,
    updatePersonDob,
    updatePersonHomeAddress,
    updatePersonPobAddress,
    updatePersonMailingAddress,
    updatePersonEmployerAddress,
    updatePersonGender,
    updatePersonNibrsGender,
    updatePersonRace,
    updatePersonHeight,
    updatePersonWeight,
    updatePersonBuild,
    updatePersonAlias,
    updatePersonHairColor,
    updatePersonEyeColor,
    updatePersonFacialHair,
    updatePersonHairLength,
    updatePersonDistinguishingCharacteristics,
    updatePersonDeceased,
    updatePersonSid,
    updatePersonFbi,
    updatePersonEmail,
    updatePersonDriversLicense,
    updatePersonLicenseState,
    updatePersonIbrGangAffiliations,
    updatePersonIbrRace,
    updatePersonEmployer,
    updatePersonGroups,
    removeAddressAlert,
    throttledAddAddressAlertMutation,
    addPersonAlert,
    removePersonAlert,
    updatePersonPhone,
    updatePersonSchool,
    addOrUpdatePersonInIncident,
  } = useContext(MutationContext);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  //Spread the personState and setup to make calls more succinct
  const {
    setupLists: {
      genders,
      nibrsGenders,
      races,
      buildTypes,
      hairColors,
      eyeColors,
      facialHairTypes,
      hairLengths,
      states,
      gangAffiliateds: gangAffiliatedss,
      groups: groupss,
      ibrArresteeRaceTypes,
    },
  } = setup;

  const handleDispatch = useCallback(
    (inIncidentAction, outOfIncidentAction, payload) => {
      if (inIncident) {
        incidentDispatch({ type: inIncidentAction, payload: payload });
      } else {
        personDispatch({ type: outOfIncidentAction, payload: payload });
      }
      return null;
    },
    [inIncident, incidentDispatch, personDispatch],
  );

  const [addOrUpdateAddress] = useMutation(ADD_OR_UPDATE_ADDRESS, {
    variables: {
      originatorId: user?.id,
      incidentId: incidentId ? incidentId : null,
    },
  });

  const handlePersonInPisChange = (event, submit = true) => {
    let name = event.target.name || event.target.value || null;
    let value = event.target.value;
    let bool = event.target.checked;
    let fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
    if (submit && typeof value === "string") {
      value = value?.trim();
    }

    if (name === "groups") {
      const payload = {
        incidentId: incidentId ? incidentId : incidentUuid,
        pisId: pis?.id ? pis.id : pis?.uuid,
        personId: pis?.person?.id,
        property: name,
        value: value,
      };
      handleDispatch(UPDATE_PERSON_IN_PIS, UPDATE_PERSON_ATTRIBUTE, payload);
    } else if (event.target.type === "checkbox") {
      const payload = {
        incidentId: incidentId ? incidentId : incidentUuid,
        pisId: pis?.id ? pis.id : pis?.uuid,
        personId: pis?.person?.id,
        property: name,
        value: bool,
      };
      handleDispatch(
        UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
        UPDATE_PERSON_PERSONAL_ATTRIBUTE,
        payload,
      );
    } else {
      const payload = {
        incidentId: incidentId ? incidentId : incidentUuid,
        pisId: pis?.id ? pis.id : pis?.uuid,
        personId: pis?.person?.id,
        property: name,
        value: value,
      };
      handleDispatch(
        UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
        UPDATE_PERSON_PERSONAL_ATTRIBUTE,
        payload,
      );
    }

    switch (name) {
      case "aliasName":
        submit &&
          updatePersonAlias({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "buildType":
        submit &&
          updatePersonBuild({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id,
              },
            },
          });
        return null;
      case "deceased":
        submit &&
          updatePersonDeceased({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: bool,
              },
            },
          });
        return null;
      case "distinguishingCharacteristics":
        submit &&
          updatePersonDistinguishingCharacteristics({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "driversLicense":
        submit &&
          updatePersonDriversLicense({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "email":
        submit &&
          updatePersonEmail({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "employer":
        submit &&
          updatePersonEmployer({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "eyeColor":
        submit &&
          updatePersonEyeColor({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "facialHairType":
        submit &&
          updatePersonFacialHair({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "fbiNumber":
        submit &&
          updatePersonFbi({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "firstName":
        submit &&
          updatePersonFirstName({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "gender":
        submit &&
          updatePersonGender({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "groups":
        submit &&
          updatePersonGroups({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: parseObjectArrayIdString(value),
              },
            },
          });
        return null;
      case "hairColor":
        submit &&
          updatePersonHairColor({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "hairLength":
        submit &&
          updatePersonHairLength({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "height":
        submit &&
          updatePersonHeight({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: +value,
              },
            },
          });
        return null;
      case "ibrArresteeRaceType":
        submit &&
          updatePersonIbrRace({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id,
              },
            },
          });
        return null;
      case "gangAffiliateds":
        submit &&
          updatePersonIbrGangAffiliations({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: parseObjectArrayIdString(value),
              },
            },
          });
        return null;
      case "juvenile":
        submit &&
          updatePersonJuvenile({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: bool,
              },
            },
          });
        return null;
      case "lastName":
        submit &&
          updatePersonLastName({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "licenseState":
        submit &&
          updatePersonLicenseState({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id,
              },
            },
          });
        return null;
      case "mailingAddress":
        return null;
      case "mentalHealthAlert":
        return null;
      case "middleName":
        submit &&
          updatePersonMiddleName({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "nibrsGender":
        submit &&
          updatePersonNibrsGender({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "notes":
        return null;
      case "organization":
        submit &&
          updatePersonOrganization({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: bool,
              },
            },
          });
        return null;
      case "race":
        submit &&
          updatePersonRace({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value.id ? value.id : "",
              },
            },
          });
        return null;
      case "residentType":
        return null;
      case "sid":
        submit &&
          updatePersonSid({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "substanceAbuseAlert":
        return null;
      case "suffix":
        submit &&
          updatePersonSuffix({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "violenceAlert":
        return null;
      case "waiverFine":
        return null;
      case "weaponsAlert":
        return null;
      case "weight":
        submit &&
          updatePersonWeight({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: +value,
              },
            },
          });
        return null;
      case "school":
        submit &&
          updatePersonSchool({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                [fieldToUpdate]: value,
              },
            },
          });
        return null;
      case "work":
        return null;

      default:
        alert(
          `Name: ${name} not found in handlePersonInPisChange function in PersonDetail.js`,
        );
        return null;
    }
  };

  const handleDateChange = (value, name, date) => {
    let fieldToUpdate = stateToMutation[name];
    let serverTimeFormat = "";
    let payload = {
      incidentId: incidentId ? incidentId : incidentUuid,
      pisId: pis?.id ? pis.id : pis?.uuid,
      personId: pis?.person?.id,
      personUuid: pis?.person?.uuid,
      pisUuid: pis?.uuid,
      property: name,
      value: serverTimeFormat,
    };
    if (!date) {
      handleDispatch(
        UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
        UPDATE_PERSON_PERSONAL_ATTRIBUTE,
        payload,
      );
      updatePersonDob({
        variables: {
          input: {
            originatorId: user?.id,
            personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
      });
    } else if (date.isValid()) {
      serverTimeFormat = date.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      payload.value = serverTimeFormat;
      handleDispatch(
        UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
        UPDATE_PERSON_PERSONAL_ATTRIBUTE,
        payload,
      );
      updatePersonDob({
        variables: {
          input: {
            originatorId: user?.id,
            personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
      });
    }
  };

  const updateAddress = (address, addressName) => {
    let addressId = address?.id ? address.id : "";
    let fieldToUpdate = stateToMutation[addressName];
    let payload = {
      incidentId: incidentId ? incidentId : incidentUuid,
      pisId: pis?.id ? pis.id : pis?.uuid,
      personId: pis?.person?.id,
      personUuid: pis?.person?.uuid,
      pisUuid: pis?.uuid,
      property: addressName,
      value: address,
    };
    // incidentDispatch({ type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS, payload:  })
    handleDispatch(
      UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      UPDATE_PERSON_PERSONAL_ATTRIBUTE,
      payload,
    );
    //if address === null send an empty string in the mutation
    switch (addressName) {
      case "homeAddress":
        updatePersonHomeAddress({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : null,
              personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
              [fieldToUpdate]: addressId,
            },
          },
        });
        return null;
      // case "pobAddress":
      //   updatePersonPobAddress({ variables: { input: { originatorId: user?.id, incidentId: incidentId ? incidentId : null,  personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid, [fieldToUpdate]: addressId } } })
      //   return null;
      case "mailingAddress":
        updatePersonMailingAddress({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : null,
              personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
              [fieldToUpdate]: addressId,
            },
          },
        });
        return null;
      case "employerAddress":
        updatePersonEmployerAddress({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : null,
              personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
              [fieldToUpdate]: addressId,
            },
          },
        });
        return null;
      default:
        alert("Address name not found in lookup");
        return null;
    }
  };

  const updatePobAddress = (e, submit) => {
    const value = e.target.value;
    const pobInputId =
      pis?.person?.personalAttribute?.pobAddress?.id || handleUuid();
    let pobInput = {
      id: pobInputId,
      name: value,
    };
    //if an id exists use that otherwise use an uuid

    submit &&
      addOrUpdateAddress({
        variables: {
          input: {
            id: pobInputId,
            streetName: value,
            city: "",
            state: "",
            streetNumber: "",
            unit: "",
            zip: "",
          },
        },
      })
        .then(res => {
          updatePersonPobAddress({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : null,
                personId: pis?.person?.id ? pis.person.id : pis?.person?.uuid,
                pobAddressId: res.data.addOrUpdateAddress.address.id,
              },
            },
          });
        })
        .catch(() => console.log("addOrUpdateAddress error"));
    //for mutations
    //addOrUpdateAddress
    //then
    //addOrUpdatePerson with the address id/uuid

    //for state
    //Add a new pobAddress with pobAddress.name = new value
    let payload = {
      personId: pis?.person?.id,
      personUuid: pis?.person?.uuid,
      property: "pobAddress",
      value: pobInput,
    };
    // incidentDispatch({ type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,  })
    handleDispatch(
      UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      UPDATE_PERSON_PERSONAL_ATTRIBUTE,
      payload,
    );
  };

  const homeAddress = useMemo(() => {
    return {
      addressName: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.name
        : "",
      streetNumber: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.streetNumber
        : "",
      streetName: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.streetName
        : "",
      unit: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.unit
        : "",
      city: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.city
        : "",
      state: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.state
        : "",
      zip: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.zip
        : "",
      lat: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.lat
        : "",
      lng: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.lng
        : "",
      validAddressAlerts: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.validAddressAlerts
        : "",
      id: pis?.person?.personalAttribute?.homeAddress
        ? pis?.person?.personalAttribute?.homeAddress.id
        : "",
    };
  }, [pis?.person?.personalAttribute?.homeAddress]);

  const mailingAddress = useMemo(() => {
    return {
      addressName: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.name
        : "",
      streetNumber: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.streetNumber
        : "",
      streetName: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.streetName
        : "",
      unit: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.unit
        : "",
      city: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.city
        : "",
      state: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.state
        : "",
      zip: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.zip
        : "",
      lat: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.lat
        : "",
      lng: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.lng
        : "",
      validAddressAlerts: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.validAddressAlerts
        : "",
      id: pis?.person?.personalAttribute?.mailingAddress
        ? pis?.person?.personalAttribute?.mailingAddress.id
        : "",
    };
  }, [pis?.person?.personalAttribute?.mailingAddress]);

  const employerAddress = useMemo(() => {
    return {
      addressName: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.name
        : "",
      streetNumber: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.streetNumber
        : "",
      streetName: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.streetName
        : "",
      unit: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.unit
        : "",
      city: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.city
        : "",
      state: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.state
        : "",
      zip: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.zip
        : "",
      lat: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.lat
        : "",
      lng: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.lng
        : "",
      validAddressAlerts: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.validAddressAlerts
        : "",
      id: pis?.person?.personalAttribute?.employerAddress
        ? pis?.person?.personalAttribute?.employerAddress.id
        : "",
    };
  }, [pis?.person?.personalAttribute?.employerAddress]);

  const handleAddPersonMatchToIncidentState = (
    incidentId,
    personIncidentSpecific,
  ) => {
    incidentDispatch({
      type: ADD_OR_UPDATE_PIS,
      payload: {
        incidentId: incidentId,
        pis: personIncidentSpecific,
      },
    });
    setPersonTab(peopleLength);
  };

  const handleAddAddressAlert = (alert, name) => {
    throttledAddAddressAlertMutation({
      variables: {
        input: {
          addressAlertId: alert.id,
          officerId: user?.id,
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : null,
          addressId: pis?.person?.personalAttribute?.[name]?.id,
          alertTypeId: alert?.alertType?.id,
          notes: alert?.notes,
          expireAt: alert?.expireAt,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = [
      ...(pis?.person?.personalAttribute?.[name]?.validAddressAlerts ?? []),
    ];
    localAlerts.push(alert);
    let localAddress = JSON.parse(
      JSON.stringify(pis?.person?.personalAttribute?.[name]),
    );
    localAddress.validAddressAlerts = localAlerts;
    // incidentDispatch({ type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS, payload:  })
    let payload = {
      personId: pis?.person?.id,
      personUuid: pis?.person?.uuid,
      property: name,
      value: localAddress,
    };
    handleDispatch(
      UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      UPDATE_PERSON_PERSONAL_ATTRIBUTE,
      payload,
    );
  };

  const handleRemoveAddressAlert = (alertId, name) => {
    removeAddressAlert({
      variables: { input: { addressAlertId: alertId, originatorId: user?.id } },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = pis?.person?.personalAttribute?.[
      name
    ]?.validAddressAlerts.filter(
      vaa => vaa.id.toString() !== alertId.toString(),
    );
    let localAddress = JSON.parse(
      JSON.stringify(pis?.person?.personalAttribute?.[name]),
    );
    localAddress.validAddressAlerts = localAlerts;
    let payload = {
      personId: pis?.person?.id,
      personUuid: pis?.person?.uuid,
      property: name,
      value: localAddress,
    };
    // incidentDispatch({ type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS, payload:  })
    handleDispatch(
      UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      UPDATE_PERSON_PERSONAL_ATTRIBUTE,
      payload,
    );
  };

  const handleAddPersonAlert = alert => {
    let localAlerts = JSON.parse(
      JSON.stringify(pis?.person?.validPersonAlerts),
    );
    localAlerts.push(alert);
    let payload = {
      incidentId: incidentId,
      incidentUuid: incidentUuid,
      pisId: pis?.id,
      pisUuid: pis?.uuid,
      personId: pis?.person?.id,
      property: "validPersonAlerts",
      value: localAlerts,
    };
    handleDispatch(UPDATE_PERSON_IN_PIS, UPDATE_PERSON_ATTRIBUTE, payload);
    addPersonAlert({
      variables: {
        input: {
          personId: pis?.person?.id,
          officerId: user?.id,
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : null,
          personAlertId: alert.id,
          alertTypeId: alert?.alertType?.id,
          notes: alert?.notes,
          expireAt: alert?.expireAt,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    }).then(response => {
      if (response?.data?.addPersonAlert) {
        if (
          response?.data?.addPersonAlert?.personAlertUuid.includes("Offline_")
        ) {
          let alertArrWithId = JSON.parse(JSON.stringify(localAlerts));
          alertArrWithId.map(la => {
            if (la.id === response?.data?.addPersonAlert?.personAlertUuid) {
              la.id = response?.data?.addPersonAlert?.personAlert.id;
            }
            return la;
          });
          let localPayload = {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            pisId: pis?.id,
            pisUuid: pis?.uuid,
            personId: pis?.person?.id,
            property: "validPersonAlerts",
            value: alertArrWithId,
          };
          handleDispatch(
            UPDATE_PERSON_IN_PIS,
            UPDATE_PERSON_ATTRIBUTE,
            localPayload,
          );
        }
      }
    });
  };

  const handleRemovePersonAlert = useCallback(
    alertId => {
      removePersonAlert({
        variables: {
          input: { personAlertId: alertId, originatorId: user?.id },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      let localAlerts = pis?.person?.validPersonAlerts.filter(
        vpa => vpa.id.toString() !== alertId.toString(),
      );
      let payload = {
        incidentId: incidentId,
        incidentUuid: incidentUuid,
        pisId: pis?.id,
        pisUuid: pis?.uuid,
        personId: pis?.person?.id,
        property: "validPersonAlerts",
        value: localAlerts,
      };
      handleDispatch(UPDATE_PERSON_IN_PIS, UPDATE_PERSON_ATTRIBUTE, payload);
    },
    [
      UPDATE_PERSON_ATTRIBUTE,
      UPDATE_PERSON_IN_PIS,
      handleDispatch,
      incidentId,
      incidentUuid,
      pis,
      removePersonAlert,
      user?.id,
    ],
  );

  const sendPhoneUpdate = (phoneNumber, propertyName) => {
    let payload = {
      incidentId: incidentId,
      pisId: pis?.id,
      personId: pis?.person?.id,
      personUuid: pis?.person?.uuid,
      pisUuid: pis?.uuid,
      property: propertyName,
      value: phoneNumber,
    };
    // incidentDispatch({ type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS, })
    handleDispatch(
      UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      UPDATE_PERSON_PERSONAL_ATTRIBUTE,
      payload,
    );
    updatePersonPhone({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : null,
          personId: pis?.person?.id,
          [propertyName]: phoneNumber,
        },
      },
    });
  };

  const startIncidentWithPerson = personId => {
    const incidentPayload = createEmptyIncident(user, false, saOris);
    const input = {
      officerId: user?.id,
      oriId: user?.homeOriId,
      uuid: incidentPayload.id,
      callTime: incidentPayload.callTime,
    };
    createNewIncident({
      variables: { input },
      context: { serializationKey: "incidentQueue" },
    }).then(res => {
      const incidentId = res?.data?.newIncident?.incidentId;
      addOrUpdatePersonInIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            personId: personId,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    });
  };

  const imgSrc = useMemo(() => {
    return pis?.person?.photos?.[pis?.person?.photos?.length - 1]
      ?.unencryptedDataUrlWrapper
      ? import.meta.env.VITE_RMS_URL +
          pis?.person?.photos?.[pis?.person?.photos?.length - 1]
            ?.unencryptedDataUrlWrapper
      : silhouette;
  }, [pis?.person?.photos]);

  useEffect(() => {
    if (!!pis?.person?.id && !pis?.person?.id.includes("Offline")) {
      getKnownAssociatesLength();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pis?.person?.id]);

  return (
    <>
      {/* <Box display="flex" flexDirection="row" justifyContent="space-between" flexWrap={'nowrap'} style={{ width: "100%" }}> */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          maxWidth: "100%",
          border: "1px solid black",
          justifyContent: "flex-start",
          alignItems: "flex-end",
        }}
      >
        <button
          type="button"
          onClick={() => setShowImageModal(true)}
          aria-label="View larger person image"
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            src={imgSrc}
            alt=""
            style={{
              width: "6vw",
              height: "6vw",
              margin: "auto 5px",
              borderRadius: "50%",
              marginTop: "2px",
            }}
          />
        </button>
        {/* {inIncident && <SingleDropDown
          handleChange={handlePisChange}
          stateName={"incidentInvolvement"}
          value={pis?.incidentInvolvement}
          options={incidentInvolvements}
          title={'Person Type'}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />}
        {inIncident && <SingleDropDown
          handleChange={handlePisChange}
          stateName={"victimType"}
          value={pis?.victimType}
          // value={""}
          options={victimTypes}
          title={'Victim Type'}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />} */}

        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={pis?.person?.personalAttribute?.organization || false}
          onChange={e => handlePersonInPisChange(e, true)}
          stateName={"organization"}
          title={"Bus."}
          userRoles={userRoles}
          {...props}
        />
        <PersonHistoryAlert
          setPopper={setPopper}
          pis={pis}
          business={pis?.person?.personalAttribute?.organization}
          showNameModal={showNameModal}
          setShowNameModal={setShowNameModal}
          showEditModal={showEditModal}
          setShowEditModal={setShowEditModal}
          incidentId={incidentId}
          incidentIndex={incidentIndex}
          pisId={pis?.id}
          pisUuid={pis?.uuid}
          pisIndex={pisIndex}
          person={pis?.person}
          addAlert={handleAddPersonAlert}
          removeAlert={handleRemovePersonAlert}
          handleIncidentOpening={handleIncidentOpening}
          handleTicketOpening={handleTicketOpening}
          handlePersonOpening={handlePersonOpening}
          handleAddPersonMatchToIncidentState={
            handleAddPersonMatchToIncidentState
          }
          showHistory={true}
          showAlert={true}
          showAliases={true}
          showEditPersonNameButton={true}
          inIncident={inIncident}
          incidentPeople={incidentPeople}
          userRoles={userRoles}
          user={user}
          setPausePolling={setPausePolling}
          tokenDisabled={tokenDisabled}
          incidentOriId={incidents[incidentIndex]?.ori?.id}
          {...props}
        />
        <FormControl
          className={classes.formControl}
          style={{ marginRight: "4vw" }}
        >
          <KeyboardDatePicker
            clearable
            data-cy={"person-detail-dob-picker-" + (pis?.person?.id || pis?.id)}
            inputProps={{
              "data-cy":
                "person-detail-dob-picker-input-" +
                (pis?.person?.id || pis?.id),
            }}
            label="DOB"
            InputLabelProps={{ shrink: true }}
            value={
              pis?.person?.personalAttribute?.dob
                ? moment
                    .utc(
                      pis?.person?.personalAttribute?.dob,
                      "YYYY-MM-DD HH:mm:ss UTC",
                    )
                    .local()
                : null
            }
            onChange={(date, value) => handleDateChange(value, "dob", date)}
            format="MM/DD/YYYY"
            style={{ width: "150px" }}
            disabled={memoizedLocked}
          />
        </FormControl>
        <TextField
          data-cy="age"
          inputProps={{ "data-cy": "age-input", size: 10 }}
          InputLabelProps={{ shrink: true }}
          id="age-id"
          label="Age"
          value={
            pis?.person?.personalAttribute?.dob &&
            moment
              .utc(
                pis?.person?.personalAttribute?.dob,
                "YYYY-MM-DD HH:mm:ss UTC",
              )
              .isValid()
              ? moment().diff(
                  moment.utc(
                    pis?.person?.personalAttribute?.dob,
                    "YYYY-MM-DD HH:mm:ss UTC",
                  ),
                  "years",
                  false,
                )
              : ""
          }
          disabled
          className={classes.formControl}
        />
        <TextField
          inputProps={{ size: 10 }}
          InputLabelProps={{ shrink: true }}
          id="age-at-incident-id"
          label="Incident&nbsp;Age"
          value={
            pis?.person?.personalAttribute?.dob &&
            moment
              .utc(
                pis?.person?.personalAttribute?.dob,
                "YYYY-MM-DD HH:mm:ss UTC",
              )
              .isValid()
              ? moment
                  .utc(incidentCallTime, "YYYY-MM-DD HH:mm:ss UTC")
                  .diff(
                    moment.utc(
                      pis?.person?.personalAttribute?.dob,
                      "YYYY-MM-DD HH:mm:ss UTC",
                    ),
                    "years",
                    false,
                  )
              : ""
          }
          disabled
          className={classes.formControl}
        />
        {/* Removed and is being replaced with Juv in the person incident detail */}
        {/* <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={pis?.person?.personalAttribute?.juvenile || false || false}
          onChange={e => handlePersonInPisChange(e, true)}
          stateName={"juvenile"}
          title={"Juvenile"}
          userRoles={userRoles}
          {...props}
        /> */}
        <AddressWithAlertAndHmi
          address={homeAddress}
          showHmi={true}
          showAlert={inIncident ? false : true}
          addressInputLabel={"Address"}
          setAddress={address => updateAddress(address, "homeAddress")}
          validAlertName={"validAddressAlerts"}
          alertType={"address"}
          deleteAlert={alertId =>
            handleRemoveAddressAlert(alertId, "homeAddress")
          }
          addAlert={alert => handleAddAddressAlert(alert, "homeAddress")}
          userRoles={userRoles}
          onFocus={() => setPausePolling(true)}
          onBlur={() => setPausePolling(false)}
          {...props}
        />
        <TextField
          data-cy="pobAddress"
          inputProps={{ "data-cy": "pobAddress-textfield-input" }}
          InputLabelProps={{ shrink: true }}
          id={pis?.person?.id + "-pobAddress"}
          label={"Place\xa0of\xa0Birth"}
          value={pis?.person?.personalAttribute?.pobAddress?.name || ""}
          onChange={e => updatePobAddress(e, false)}
          onBlur={e => updatePobAddress(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"gender"}
          value={pis?.person?.personalAttribute?.gender}
          options={genders}
          title={"Sex"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        {/* Add NIBRS Gender options when available */}
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"nibrsGender"}
          value={pis?.person?.personalAttribute?.nibrsGender}
          options={nibrsGenders}
          title={"NIBRS Gender"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"race"}
          value={pis?.person?.personalAttribute?.race}
          options={races}
          title={"Race"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="Height"
          inputProps={{ "data-cy": "height-input" }}
          InputLabelProps={{ shrink: true }}
          id="height-id"
          label="Height"
          type="number"
          value={pis?.person?.personalAttribute?.height || ""}
          name={"height"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <TextField
          data-cy="Weight"
          inputProps={{ "data-cy": "weight-input" }}
          InputLabelProps={{ shrink: true }}
          id="weight-id"
          label="Weight"
          type="number"
          value={pis?.person?.personalAttribute?.weight || ""}
          name={"weight"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"buildType"}
          value={pis?.person?.personalAttribute?.buildType}
          options={buildTypes}
          title={"Build"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="Aliases"
          inputProps={{ "data-cy": "aliases-input" }}
          InputLabelProps={{ shrink: true }}
          id="aliases-id"
          label="Nicknames"
          value={pis?.person?.personalAttribute?.aliasName || ""}
          name={"aliasName"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"hairColor"}
          value={pis?.person?.personalAttribute?.hairColor}
          options={hairColors}
          title={"Hair"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"eyeColor"}
          value={pis?.person?.personalAttribute?.eyeColor || null}
          options={eyeColors}
          title={"Eyes"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"facialHairType"}
          value={pis?.person?.personalAttribute?.facialHairType}
          options={facialHairTypes}
          title={"Facial\xa0Hair"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth || 200}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"hairLength"}
          value={pis?.person?.personalAttribute?.hairLength}
          options={hairLengths}
          title={"Hair Length"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="DistinguishingCharacteristics"
          inputProps={{ "data-cy": "distinguishingCharacteristics-input" }}
          InputLabelProps={{ shrink: true }}
          id="distinguishingCharacteristics-id"
          label="Scars/Tattoos"
          value={
            pis?.person?.personalAttribute?.distinguishingCharacteristics || ""
          }
          name={"distinguishingCharacteristics"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={pis?.person?.personalAttribute?.deceased || false}
          onChange={e => handlePersonInPisChange(e, true)}
          stateName={"deceased"}
          title={"Deceased"}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="SID"
          inputProps={{ "data-cy": "sid-input" }}
          InputLabelProps={{ shrink: true }}
          id="sid-id"
          label="SID"
          value={pis?.person?.personalAttribute?.sid || ""}
          name={"sid"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <TextField
          data-cy="FbiNumber"
          inputProps={{ "data-cy": "fbiNumber-input" }}
          InputLabelProps={{ shrink: true }}
          id="fbiNumber-id"
          label="FBI#"
          value={pis?.person?.personalAttribute?.fbiNumber || ""}
          name={"fbiNumber"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <FormControl style={{ color: "black" }} className={classes.formControl}>
          <PhoneInput
            setPhoneNumber={sendPhoneUpdate}
            phoneString={pis?.person?.personalAttribute?.phone || ""}
            pisId={pis?.id ? pis.id : pis?.uuid}
            personId={pis?.person?.id ? pis?.person?.id : pis?.person?.uuid}
            modalClass={classes.modal}
            inputClass={classes.modalInput}
            paperClass={classes.paper}
            title={"Primary Phone"}
            propertyName={"phone"}
            userRoles={userRoles}
            {...props}
          />
        </FormControl>
        <FormControl style={{ color: "black" }} className={classes.formControl}>
          <PhoneInput
            setPhoneNumber={sendPhoneUpdate}
            phoneString={pis?.person?.personalAttribute?.cell || ""}
            pisId={pis?.id ? pis.id : pis?.uuid}
            personId={pis?.person?.id ? pis?.person?.id : pis?.person?.uuid}
            modalClass={classes.modal}
            inputClass={classes.modalInput}
            paperClass={classes.paper}
            title={"Secondary Phone"}
            propertyName={"cell"}
            userRoles={userRoles}
            {...props}
          />
        </FormControl>
        <TextField
          data-cy="Email"
          inputProps={{ "data-cy": "email-input" }}
          InputLabelProps={{ shrink: true }}
          id="email-id"
          label="Email"
          value={pis?.person?.personalAttribute?.email || ""}
          name={"email"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <LicenseNumberAndState
          handleChange={handlePersonInPisChange}
          state={pis?.person?.personalAttribute?.licenseState}
          stateOptions={states}
          userRoles={userRoles}
          driversLicense={pis?.person?.personalAttribute?.driversLicense}
          disabled={memoizedLocked}
          {...props}
        />
        <AddressWithAlertAndHmi
          address={mailingAddress}
          showHmi={false}
          showAlert={false}
          addressInputLabel={"Mailing\xa0Address"}
          setAddress={address => updateAddress(address, "mailingAddress")}
          validAlertName={"validAddressAlerts"}
          alertType={"address"}
          deleteAlert={alertId =>
            handleRemoveAddressAlert(alertId, "mailingAddress")
          }
          addAlert={alert => handleAddAddressAlert(alert, "mailingAddress")}
          userRoles={userRoles}
          onFocus={() => setPausePolling(true)}
          onBlur={() => setPausePolling(false)}
          containerStyle={{ margin: "8px" }}
          {...props}
        />
        <TextField
          data-cy="School"
          inputProps={{ "data-cy": "school-input" }}
          InputLabelProps={{ shrink: true }}
          id="school-id"
          label="School"
          value={pis?.person?.personalAttribute?.school || ""}
          name={"school"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <MultiDropDownObjects
          formStyle={classes.formControl}
          labelStyle={classes.multiInputLabel}
          selectStyle={null}
          chipsStyle={classes.chips}
          chipStyle={classes.chip}
          id={"gangAffiliateds"}
          title={"IBR Gang Affil."}
          stateName={"gangAffiliateds"}
          handleChange={handlePersonInPisChange}
          selectedValues={
            pis?.person?.personalAttribute?.gangAffiliateds
              ? gangAffiliatedss.filter(option =>
                  pis?.person?.personalAttribute?.gangAffiliateds.some(
                    ibrGangAff => option.id === ibrGangAff.id,
                  ),
                )
              : []
          }
          options={gangAffiliatedss}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handlePersonInPisChange}
          stateName={"ibrArresteeRaceType"}
          value={pis?.person?.personalAttribute?.ibrArresteeRaceType}
          options={ibrArresteeRaceTypes}
          title={"Ethnicity"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="Employer"
          inputProps={{ "data-cy": "employer-input" }}
          InputLabelProps={{ shrink: true }}
          id="employer-id"
          label="Employer"
          value={pis?.person?.personalAttribute?.employer || ""}
          name={"employer"}
          onChange={e => handlePersonInPisChange(e, false)}
          onBlur={e => handlePersonInPisChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          disabled={memoizedLocked}
        />
        <AddressWithAlertAndHmi
          address={employerAddress}
          showHmi={false}
          showAlert={false}
          addressInputLabel={"Employer\xa0Address"}
          setAddress={address => updateAddress(address, "employerAddress")}
          validAlertName={"validAddressAlerts"}
          alertType={"address"}
          deleteAlert={alertId =>
            handleRemoveAddressAlert(alertId, "employerAddress")
          }
          addAlert={alert => handleAddAddressAlert(alert, "employerAddress")}
          userRoles={userRoles}
          onFocus={() => setPausePolling(true)}
          onBlur={() => setPausePolling(false)}
          {...props}
        />

        <MultiDropDownObjects
          formStyle={classes.formControl}
          labelStyle={classes.multiInputLabel}
          selectStyle={null}
          chipsStyle={classes.chips}
          chipStyle={classes.chip}
          id={"groups"}
          title={"Groups"}
          stateName={"groups"}
          handleChange={handlePersonInPisChange}
          selectedValues={
            pis?.person?.groups
              ? groupss.filter(option =>
                  pis?.person?.groups.some(group => option.id === group.id),
                )
              : []
          }
          options={groupss}
          userRoles={userRoles}
          {...props}
        />
        <br />
        {inIncident && (
          <PersonIncidentSpecific
            user={user}
            pis={pis}
            incidentId={incidentId}
            incidentUuid={incidentUuid}
            incidentIndex={incidentIndex}
            incidentOriId={incidentOriId}
            incidentCallTime={incidentCallTime}
            personIndex={personIndex}
            personTab={personTab}
            setPersonTab={setPersonTab}
            userRoles={userRoles}
            disabled={memoizedLocked}
            setPopper={setPopper}
            {...props}
          />
        )}
        <Attachments
          parentId={pis?.person?.id}
          parentName={formatNameFromPersonObj(pis?.person) || ""}
          incidentId={incidentId || ""}
          uploadClass={"Person"}
          secondarySiblingIds={user?.secondarySiblingIds}
          memoizedLocked={memoizedLocked}
          userId={user?.id}
          primaryOfficerId={inIncident ? primaryOfficerId : null}
          userRoles={userRoles}
          online={onlineState}
          {...props}
        />
        <br />
      </div>
      <PersonDetailButtons
        inIncident={inIncident}
        onlineState={onlineState}
        locked={memoizedLocked}
        addPersonClick={handleAddClick}
        removePersonClick={handleRemoveClick}
        pisId={pis?.id ? pis?.id : pis?.uuid}
        personId={pis?.person?.id}
        searchClick={setShowPersonSearch}
        historyClick={setShowHistoryModal}
        relationshipClick={setShowRelationshipModal}
        startIncident={startIncidentWithPerson}
        user={user}
        knownAssociatesNumber={knownAssociatesNumber}
      />
      <PersonHistoryModal
        business={pis?.person?.personalAttribute?.organization}
        modalOpen={showHistoryModal}
        setModalOpen={setShowHistoryModal}
        handleIncidentOpening={handleIncidentOpening}
        handleTicketOpening={handleTicketOpening}
        handlePersonOpening={handlePersonOpening}
        person={pis?.person}
        userRoles={userRoles}
        incidentId={incidentId}
        handleAddPersonMatchToIncidentState={
          handleAddPersonMatchToIncidentState
        }
        knownAssociatesNumber={knownAssociatesNumber}
        {...props}
      />
      <RelationshipModal
        modalOpen={showRelationshipModal}
        setModalOpen={setShowRelationshipModal}
        personId={pis?.person?.id}
        userRoles={userRoles}
        canEdit={props.canEdit}
        setPausePolling={setPausePolling}
      />
      <PersonSearch
        showModal={showPersonSearch}
        setShowModal={setShowPersonSearch}
        handlePersonOpening={handlePersonOpening}
        handleAddPersonMatchToIncidentState={
          handleAddPersonMatchToIncidentState
        }
        incidentId={incidentId}
        userRoles={userRoles}
        incidentPeople={incidentPeople}
        {...props}
      />
      <Modal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        className={classes.modal}
        data-cy="person-image-modal"
        keepMounted={false}
        style={{ maxWidth: "75vw", margin: "auto" }}
      >
        <div>
          <ImageCarousel imageArr={pis?.person?.photos} reverse={true} />
        </div>
      </Modal>
    </>
  );
};

export default PersonDetailWrapper;
